// UGC-ads wizard shell (Phase 2).
//
// Entry: /ugc-wizard?ugc=<mediaId>. Deep-links from the UGC Ads page
// (Phase 4) and the Media Library carry the target UGC id in ?ugc=.
// Without a ?ugc= param the wizard opens on Step 1 (UGC picker, 2b);
// with one it fast-forwards to Step 2 (target).
//
// Step state is URL-driven (?step=<key>) so back/forward + refresh
// preserve position; the campaign anchor stays too (?campaignId=) so
// deep-links from Campaigns → wizard hydrate cleanly.
//
// State machine — five steps regardless of path (2c/d/e/f share the
// same skeleton; Step 2's picker morphs per path):
//   ugc → target → campaign → quickcreate? → confirm
// Step 4 (quickcreate) is conditional — only visits when operator
// picks +new campaign at Step 3.
//
// Sub-phase status:
//   2a — shell + preview rail + step routing + footer   — shipped
//   2b — Step 1 UGC picker                              — shipped
//   2c — Product path (Steps 2-5)                       — shipped
//   2d — Branding path                                  — shipped
//   2e — Promotional path                               — shipped
//   2f — Category variant                               — shipped (collection quick-create disabled)
//
// Phase 3 backend cascade (UGC_FIRST_SEEDING) landed 2026-08-10 — the
// preview rail's "UGC-first seeding activates when..." caveat in Step 5
// is now live. Field passed on every generate; kill-switch controls
// server-side behaviour.
//
// Backend touchpoints:
//   GET  /api/media                       — 2b UGC list
//   GET  /api/media/:id/assignments       — preview rail + pre-check state (Phase 1 API)
//   POST /api/media/:id/assign            — persist attachments on toggle
//   DELETE /api/media/:id/assign          — detach on toggle
//   GET  /api/catalog                     — product picker (2c/2e callouts)
//   GET  /api/catalog/categories          — category picker (2f)
//   GET  /api/campaigns                   — Step 3 campaign list
//   POST /api/campaigns                   — Step 4 quick-create
//   POST /api/ads/generate                — Step 5 confirm

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, HStack, VStack, Flex, Button, Text, Badge, useToast, Select
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { PageHeader } from '../../shell/PageHeader';
import { UgcPreviewRail } from './UgcPreviewRail';
import { Step1UgcPicker } from './Step1UgcPicker';
import { Step2Target } from './Step2Target';
import { Step3Campaign } from './Step3Campaign';
import { Step4QuickCreate } from './Step4QuickCreate';
import { Step5Confirm } from './Step5Confirm';
import { MAX_UGCS_PER_BATCH } from './types';
import type {
  MediaAssignments, WizardSelections, WizardStepKey
} from './types';
// Same platform picker Generate Ads uses. Persistent above the step
// body so operators can flip between Reels 9:16 / Meta 1:1 / etc. at
// any point without walking backwards through the wizard.
import { PlatformFormatPicker } from '../GenerateAds';
import { useFormatCatalog, isFormatLive } from '../GenerateAds/formatCatalog';

const STEPS: Array<{ key: WizardStepKey; label: string; description: string }> = [
  { key: 'ugc',         label: 'UGC',         description: 'Choose the source post' },
  { key: 'target',      label: 'Target',      description: 'Product, category, brand, or promotional' },
  { key: 'campaign',    label: 'Campaign',    description: 'Pick or create' },
  { key: 'quickcreate', label: 'New campaign',description: 'Name + description' },
  { key: 'confirm',     label: 'Confirm',     description: 'Preview + generate' }
];

function stepIndex(k: WizardStepKey): number {
  return STEPS.findIndex(s => s.key === k);
}

// All five ai_* templates run per seed — matches DEFAULT_TEMPLATE_IDS
// in the Generate Ads wizard. Backend chooses the actual template via
// Director's creative_style.
const DEFAULT_TEMPLATE_IDS = [
  'ai_brand_led',
  'ai_ugc_led',
  'ai_social_proof_led',
  'ai_editorial',
  'ai_promotional'
];

export function UgcAdsWizard() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { activeBrand } = useBrand();

  // ?ugc=<id> — single-UGC deep-link from the Media Library / Phase 4
  // UGC Ads page. ?ugcIds=a,b,c — Phase 7 batch deep-link (currently
  // unused by any surface but supported for symmetry).
  const initialMediaIds = (() => {
    const single = params.get('ugc');
    const batch  = (params.get('ugcIds') || '').split(',').map(s => s.trim()).filter(Boolean);
    if (batch.length) return batch.slice(0, MAX_UGCS_PER_BATCH);
    if (single)       return [single];
    return [];
  })();
  const initialCampaign = params.get('campaignId');

  const stepFromUrl = (params.get('step') as WizardStepKey) || (initialMediaIds.length ? 'target' : 'ugc');
  const step: WizardStepKey = STEPS.some(s => s.key === stepFromUrl) ? stepFromUrl : 'ugc';

  const [selections, setSelections] = useState<WizardSelections>({
    mediaIds:           initialMediaIds,
    path:               'product',
    productIds:         [],
    categoryIds:        [],
    calloutProductIds:  [],
    campaignId:         initialCampaign,
    newCampaign:        { name: '', description: '', offer: '', endDate: '' },
    batchAcknowledged:  initialMediaIds.length <= 1,  // single-UGC auto-true
    // Single-surface default matches Generate Ads — the operator
    // opts INTO multi-surface fan-out. Prevents a surprise 5x cost
    // multiplier on batch UGC runs.
    staticFormatKeys:   ['meta_feed_1_1'],
    videoFormatKeys:    ['meta_feed_1_1'],
    videoDurationSec:   10
  });

  // Assignments store — keyed by mediaId so batch mode can compute
  // intersections without one round-trip per checkbox toggle. Single
  // primary (mediaIds[0]) drives the preview rail; the whole map is
  // handed to Step 2 for pre-check state.
  const [assignmentsByMedia, setAssignmentsByMedia] = useState<Map<string, MediaAssignments>>(new Map());
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError]     = useState<string | null>(null);
  const [assignmentsBump, setAssignmentsBump]       = useState(0);
  const [generating, setGenerating]                 = useState(false);

  // Live format catalog (GET /api/ads/formats) — same hook Generate
  // Ads uses. Feeds the picker + drives the snap effect below.
  const formatCatalog = useFormatCatalog();

  // Snap stale format ticks to live surfaces after catalog load —
  // matches Generate Ads' behaviour. A coming_soon (or unknown) key
  // that survived a deploy would otherwise fail the /generate call
  // with PLATFORM_FORMAT_COMING_SOON.
  useEffect(() => {
    if (formatCatalog.loading) return;
    const liveKeys = new Set(formatCatalog.formats
      .filter(f => isFormatLive(f))
      .map(f => f.key));
    const filteredStatic = selections.staticFormatKeys.filter(k => liveKeys.has(k));
    const filteredVideo  = selections.videoFormatKeys.filter(k => liveKeys.has(k));
    if (
      filteredStatic.length !== selections.staticFormatKeys.length ||
      filteredVideo.length  !== selections.videoFormatKeys.length
    ) {
      setSelections(prev => ({
        ...prev,
        staticFormatKeys: filteredStatic.length ? filteredStatic : ['meta_feed_1_1'],
        videoFormatKeys:  filteredVideo
      }));
    }
  }, [formatCatalog.loading, formatCatalog.formats, selections.staticFormatKeys, selections.videoFormatKeys]);

  const mediaIdsKey = selections.mediaIds.join(',');

  useEffect(() => {
    if (selections.mediaIds.length === 0) {
      setAssignmentsByMedia(new Map());
      setAssignmentsError(null);
      return;
    }
    setAssignmentsLoading(true);
    setAssignmentsError(null);
    let cancelled = false;
    void (async () => {
      try {
        // Parallel fetch — MAX_UGCS_PER_BATCH=20 is small enough to fan
        // out without a limiter. One failure fails the whole set;
        // callers should re-open the wizard rather than silently
        // proceed on partial data (Step 2 pre-check state would be
        // wrong for the missing UGC).
        const entries = await Promise.all(
          selections.mediaIds.map(async id => {
            const r = await apiJson<MediaAssignments>(`/api/media/${id}/assignments`);
            return [id, r] as const;
          })
        );
        if (!cancelled) setAssignmentsByMedia(new Map(entries));
      } catch (err) {
        if (!cancelled) setAssignmentsError(err instanceof Error ? err.message : 'Failed to load UGC');
      } finally {
        if (!cancelled) setAssignmentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mediaIdsKey, assignmentsBump]);

  // Single-UGC compat for the preview rail — first UGC is the "primary"
  // that drives the rail thumbnail + attachment summary. Batch mode
  // shows a count + strip too (see UgcPreviewRail).
  const primaryAssignments = selections.mediaIds[0]
    ? assignmentsByMedia.get(selections.mediaIds[0]) || null
    : null;

  const update = (patch: Partial<WizardSelections>) =>
    setSelections(prev => ({ ...prev, ...patch }));

  const goTo = (k: WizardStepKey) => {
    const next = new URLSearchParams(params);
    next.set('step', k);
    if (selections.mediaIds.length === 1) {
      next.set('ugc', selections.mediaIds[0]);
      next.delete('ugcIds');
    } else if (selections.mediaIds.length > 1) {
      next.set('ugcIds', selections.mediaIds.join(','));
      next.delete('ugc');
    } else {
      next.delete('ugc');
      next.delete('ugcIds');
    }
    if (selections.campaignId) next.set('campaignId', selections.campaignId);
    setParams(next, { replace: false });
  };

  const linearOrder: WizardStepKey[] = ['ugc', 'target', 'campaign', 'quickcreate', 'confirm'];
  const idx = linearOrder.indexOf(step);
  const isCreatingNew = !selections.campaignId && !!selections.newCampaign.name.trim();

  const back = () => {
    if (idx <= 0) return;
    // Skip quickcreate on back-nav when nothing was drafted there.
    const prevKey = step === 'confirm' && !isCreatingNew ? 'campaign' : linearOrder[idx - 1];
    goTo(prevKey);
  };

  const next = () => {
    if (idx < 0 || idx >= linearOrder.length - 1) return;
    // Skip quickcreate on forward-nav when the operator already picked
    // an existing campaign at Step 3.
    const nextKey = step === 'campaign' && !isCreatingNew ? 'confirm' : linearOrder[idx + 1];
    goTo(nextKey);
  };

  const canProceed = useMemo(() => {
    if (step === 'ugc')     return selections.mediaIds.length > 0 && selections.mediaIds.length <= MAX_UGCS_PER_BATCH;
    if (step === 'target') {
      if (selections.path === 'product')     return selections.productIds.length > 0;
      if (selections.path === 'category')    return selections.categoryIds.length > 0;
      // Batch mode branding/promotional — require ALL selected UGCs to
      // have the assignment persisted, matching the ProductPicker's
      // "attach to every selected UGC" contract.
      if (selections.path === 'brand') {
        return selections.mediaIds.every(id => !!assignmentsByMedia.get(id)?.branding);
      }
      if (selections.path === 'promotional') {
        return selections.mediaIds.every(id => !!assignmentsByMedia.get(id)?.promotional);
      }
    }
    if (step === 'campaign')    return !!selections.campaignId || isCreatingNew;
    if (step === 'quickcreate') {
      const kind = selections.path === 'category' ? 'collection' : selections.path;
      if (kind === 'collection') return false; // POST /api/campaigns doesn't accept collection
      return !!selections.newCampaign.name.trim();
    }
    if (step === 'confirm')     return selections.batchAcknowledged;
    return false;
  }, [step, selections, assignmentsByMedia, isCreatingNew]);

  const isLastStep = step === 'confirm';

  const handleGenerate = async () => {
    if (!activeBrand?.id) {
      toast({ title: 'No active brand', status: 'error' });
      return;
    }
    if (selections.mediaIds.length === 0) {
      toast({ title: 'Missing UGC', status: 'error' });
      return;
    }
    setGenerating(true);
    try {
      // 1. Resolve campaignId — mint one if the operator drafted a new campaign.
      let campaignId = selections.campaignId;
      if (!campaignId && isCreatingNew) {
        const kind = selections.path === 'category' ? 'collection' : selections.path;
        if (kind === 'collection') {
          throw new Error('Collection campaigns must be picked from Step 3 (not creatable in-app yet)');
        }
        const body: Record<string, unknown> = {
          name: selections.newCampaign.name.trim(),
          kind
        };
        if (kind === 'product') {
          body.productIds = selections.productIds;
        }
        if (kind === 'promotional') {
          body.productIds = selections.calloutProductIds;
          if (selections.newCampaign.offer || selections.newCampaign.endDate) {
            body.promotionalDetails = {
              offer:  selections.newCampaign.offer || null,
              endsAt: selections.newCampaign.endDate || null,
              description: selections.newCampaign.description || null
            };
          }
        }
        const created = await apiJson<{ campaign: { id: string } }>(
          `/api/campaigns?brandId=${encodeURIComponent(activeBrand.id)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          }
        );
        campaignId = created.campaign?.id || null;
        if (!campaignId) throw new Error('Campaign create returned no id');
      }
      if (!campaignId) throw new Error('No campaign selected');

      // 2. Batch dispatch — Phase 7 W1: each UGC seeds its OWN
      //    generation (preferUgcMediaId varies per UGC), so we call
      //    /api/ads/generate ONCE PER UGC × campaign. For the current
      //    single-campaign wizard UX, that means N calls total.
      //
      //    Sequential (not Promise.all) because the backend's
      //    concurrency gate refuses overlapping runs on the same
      //    campaign+product set within the stale window — parallel
      //    dispatch here would race the gate and lose N-1 of them to
      //    409s. See services/generationGate.js.
      const productIdsForGenerate =
        selections.path === 'product'     ? selections.productIds :
        selections.path === 'promotional' ? selections.calloutProductIds :
        [];

      const runIds: string[] = [];
      let totalAds = 0;
      const failures: string[] = [];
      // preset:'explicit' + staticFormatKeys + videoFormatKeys is the
      // same billable model Generate Ads uses. Backend
      // (resolveExplicitFormats) is the authority on per-platform
      // video clamping — a Meta multi-tick still collapses to one
      // master, Google gets one master per aspect.
      const includeVideo = selections.videoFormatKeys.length > 0;
      for (const mediaId of selections.mediaIds) {
        const generateBody: Record<string, unknown> = {
          campaignId,
          mediaIds:    [mediaId],
          productIds:  productIdsForGenerate,
          templateIds: DEFAULT_TEMPLATE_IDS,
          preset:      'explicit',
          staticFormats: selections.staticFormatKeys,
          videoFormats:  selections.videoFormatKeys,
          expandStaticFormats: false,
          preferUgcMediaId: mediaId
        };
        if (includeVideo) generateBody.videoDurationSec = selections.videoDurationSec;
        try {
          const res = await apiJson<{ campaignRunId: string; total: number }>(
            `/api/ads/generate?brandId=${encodeURIComponent(activeBrand.id)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(generateBody)
            }
          );
          runIds.push(res.campaignRunId);
          totalAds += res.total || 0;
        } catch (err) {
          // Partial failure — record which UGC failed and continue.
          // Better UX than aborting the whole batch on run 3 of 20.
          const msg = err instanceof Error ? err.message : 'unknown error';
          failures.push(`${mediaId.slice(-6)}: ${msg}`);
        }
      }

      if (runIds.length === 0) {
        throw new Error(`All ${selections.mediaIds.length} dispatches failed. ${failures[0] || ''}`);
      }
      if (failures.length > 0) {
        toast({
          title: `${runIds.length}/${selections.mediaIds.length} dispatches queued`,
          description: `Failed: ${failures.join('; ')}`,
          status: 'warning',
          duration: 8000
        });
      } else {
        toast({
          title: 'Generation started',
          description: selections.mediaIds.length === 1
            ? `Queued ${totalAds} ad${totalAds === 1 ? '' : 's'}.`
            : `Queued ${totalAds} ad${totalAds === 1 ? '' : 's'} across ${runIds.length} UGC${runIds.length === 1 ? '' : 's'}.`,
          status: 'success'
        });
      }
      // Redirect: single run → the standard run-status view.
      // Batch → the Ads page filtered by campaign (each runId's ads
      // will still show up there as they render). Passing the first
      // run keeps the campaignRunId poller working for at least one.
      navigate(`/ads?campaignRunId=${runIds[0]}`);
    } catch (err) {
      toast({
        title: 'Generate failed',
        description: err instanceof Error ? err.message : 'unknown error',
        status: 'error',
        duration: 6000
      });
    } finally {
      setGenerating(false);
    }
  };

  const nextLabel = (() => {
    const nextKey = step === 'campaign' && !isCreatingNew ? 'confirm' : linearOrder[idx + 1];
    const nextDef = STEPS.find(s => s.key === nextKey);
    return `Next: ${nextDef?.label || ''}`;
  })();

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        eyebrow="UGC Ads"
        title="Generate ads from a UGC post"
        description="Attach a social post to a product, category, brand identity, or promotional target — then generate ads against a matching campaign."
      />

      <Stepper current={step} />

      {/* Platform picker — persistent above the step body, matching
          Generate Ads UX. Only visible once at least one UGC is picked
          (Step 1 is about choosing the UGC, not the surface). Operator
          can flip Reels 9:16 ↔ Meta 1:1 ↔ etc. from any subsequent
          step without walking backwards. */}
      {step !== 'ugc' && selections.mediaIds.length > 0 && (
        <>
          <PlatformFormatPicker
            staticKeys={selections.staticFormatKeys}
            videoKeys={selections.videoFormatKeys}
            onSelectionChange={({ staticKeys, videoKeys }) => update({
              staticFormatKeys: staticKeys,
              videoFormatKeys:  videoKeys
            })}
            formats={formatCatalog.formats}
            fromFallback={formatCatalog.fromFallback}
          />
          {selections.videoFormatKeys.length > 0 && (
            <UgcVideoLengthStrip
              value={selections.videoDurationSec}
              onChange={(v) => update({ videoDurationSec: v })}
            />
          )}
        </>
      )}

      <Flex gap={6} align="flex-start">
        <UgcPreviewRail
          assignments={primaryAssignments}
          assignmentsByMedia={assignmentsByMedia}
          mediaIds={selections.mediaIds}
          loading={assignmentsLoading}
          error={assignmentsError}
        />

        <Box flex={1} minW={0}>
          {step === 'ugc' && (
            <Step1UgcPicker
              selectedMediaIds={selections.mediaIds}
              onChangeMediaIds={(ids) => update({
                mediaIds: ids,
                // Auto-toggle the batch acknowledgement — single-UGC
                // is auto-true, batch requires explicit tick at Step 5.
                batchAcknowledged: ids.length <= 1
              })}
            />
          )}
          {step === 'target' && (
            <Step2Target
              value={selections}
              onChange={update}
              assignments={primaryAssignments}
              assignmentsByMedia={assignmentsByMedia}
              onReloadAssignments={() => setAssignmentsBump(b => b + 1)}
            />
          )}
          {step === 'campaign' && (
            <Step3Campaign
              value={selections}
              onChange={update}
              onGoToQuickCreate={() => goTo('quickcreate')}
            />
          )}
          {step === 'quickcreate' && (
            <Step4QuickCreate
              value={selections}
              onChange={update}
            />
          )}
          {step === 'confirm' && (
            <Step5Confirm
              value={selections}
              onChange={update}
              assignments={primaryAssignments}
              assignmentsByMedia={assignmentsByMedia}
            />
          )}
        </Box>
      </Flex>

      <HStack justify="space-between" pt={2}>
        <Button as={RouterLink} to="/media-library" variant="ghost" size="sm">
          Cancel
        </Button>
        <HStack spacing={2}>
          {idx > 0 && (
            <Button onClick={back} variant="outline" size="sm" isDisabled={generating}>Back</Button>
          )}
          {!isLastStep && (
            <Button
              onClick={next}
              variant="brand"
              size="sm"
              isDisabled={!canProceed}
            >
              {nextLabel}
            </Button>
          )}
          {isLastStep && (
            <Button
              variant="brand"
              size="sm"
              isDisabled={!canProceed || generating}
              isLoading={generating}
              loadingText="Queueing…"
              onClick={handleGenerate}
            >
              Generate
            </Button>
          )}
        </HStack>
      </HStack>
    </VStack>
  );
}

function Stepper({ current }: { current: WizardStepKey }) {
  const idx = stepIndex(current);
  return (
    <Flex
      borderWidth="1px"
      borderColor="brand.border"
      borderRadius="lg"
      bg="brand.surface"
      px={4}
      py={3}
      gap={3}
      align="center"
      wrap="wrap"
    >
      {STEPS.map((s, i) => {
        const status = i < idx ? 'done' : i === idx ? 'active' : 'pending';
        return (
          <HStack key={s.key} spacing={2} flex={1} minW="140px">
            <Box
              w="24px" h="24px"
              borderRadius="full"
              bg={status === 'active' ? 'rsViolet.500' : status === 'done' ? 'green.400' : 'gray.200'}
              color="white"
              display="flex" alignItems="center" justifyContent="center"
              fontSize="xs" fontWeight="800"
              flexShrink={0}
            >
              {status === 'done' ? '✓' : i + 1}
            </Box>
            <Box minW={0}>
              <Text
                fontSize="sm"
                fontWeight="700"
                color={status === 'active' ? 'rsViolet.700' : 'brand.ink'}
                noOfLines={1}
              >
                {s.label}
              </Text>
              <Text fontSize="10px" color="brand.muted" noOfLines={1}>{s.description}</Text>
            </Box>
            {status === 'active' && <Badge fontSize="9px" colorScheme="purple" variant="subtle">Now</Badge>}
          </HStack>
        );
      })}
    </Flex>
  );
}

// Compact video-length picker. Only mounted when videoFormatKeys is
// non-empty, so brand-only static-only runs stay uncluttered. The
// Generate Ads wizard exposes a broader VideoControlsStrip (director
// variants + prompt guidance + raw editor) — for UGC MVP a plain
// duration dropdown is enough; the UGC IS the base plate, so the
// canonical prompt + storyboard aren't operator-facing here.
function UgcVideoLengthStrip({
  value, onChange
}: {
  value:    number;
  onChange: (v: number) => void;
}) {
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" bg="brand.surface" p={4}>
      <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
        Video length
      </Text>
      <Select
        size="sm"
        maxW="200px"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        <option value={4}>4s</option>
        <option value={6}>6s</option>
        <option value={8}>8s</option>
        <option value={10}>10s (standard)</option>
        <option value={12}>12s</option>
        <option value={15}>15s</option>
      </Select>
      <Text fontSize="xs" color="brand.muted" mt={1}>
        Google Omni renders 4 / 6 / 8 / 10s — other lengths snap to the nearest supported duration server-side.
      </Text>
    </Box>
  );
}
